import { createSelector } from 'reselect';

import { Point2D as Point2DInterface, SnappingUnderlay } from '@paragon/contract-library';
import { LineSegment2D, Point2D } from '@paragon/geometry-library';
import { getBoundsFromPoints } from '@paragon/three-shared/lib/getBoundsFromPoints';
import { notNullish, pairwise } from '@paragon/utilities';

import { VisibilityConfig, VisibilityConfiguration } from '../../library/hierarchy-filtering/visible-objects';
import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { RootState } from '../index';
import { getAllSnappingUnderlaysDictionary } from './layoutDataSelectors';

const PDF_POINTS_PER_INCH = 72;

function mapPathPoints<T, U>(paths: readonly (readonly T[])[], mapFn: (point: T) => U): U[][] {
  return paths.map((path) => path.map(mapFn));
}

function computeLinesForUnderlay(
  extractedPaths: SnappingUnderlay['extractedPaths'],
  scale: number,
  translate: Point2DInterface,
  rotation: number,
): LineSegment2D[] {
  let paths = mapPathPoints(extractedPaths, Point2D.fromObject);
  if (paths.length === 0) return [];

  // scale
  paths = mapPathPoints(paths, (point) => point.multiplyScalar(scale / PDF_POINTS_PER_INCH));

  // rotate
  if (rotation !== 0) {
    const center = getBoundsFromPoints(paths.flat()).position;
    paths = mapPathPoints(paths, (point) => point.rotateAround(center, rotation));
  }

  // translate
  paths = mapPathPoints(paths, (point) => point.add(translate));

  // explode paths into segments
  return paths.flatMap((path) =>
    pairwise(path)
      .map(([start, end]) => (start.equals(end) ? null : new LineSegment2D(start, end)))
      .filter(notNullish),
  );
}

export const getConfiguredSnappingUnderlaysVisible = (state: RootState) => state.visualOptions.snappingUnderlaysVisible;

export const getActiveSnappingUnderlaysVisible = createSelector(
  getConfiguredSnappingUnderlaysVisible,
  getSelectedItemIdentifiers,
  (configuredVisible, selectedItems) =>
    configuredVisible || selectedItems.some((item) => item.type === 'snappingUnderlay'),
);

export const getConfiguredUnderlayVisibilityConfiguration = (state: RootState): VisibilityConfiguration =>
  state.snappingUnderlay.visibleUnderlayConfiguration;

export const getActiveUnderlayVisibility = createSelector(
  getActiveSnappingUnderlaysVisible,
  getConfiguredUnderlayVisibilityConfiguration,
  getSelectedItemIdentifiers,
  (globallyVisible, config, selectedItems): ((guid: string) => boolean) => {
    if (!globallyVisible) return () => false;

    const selectedUnderlayGuids = new Set(
      selectedItems.filter((item) => item.type === 'snappingUnderlay').map((item) => item.guid),
    );

    return (guid: string) => selectedUnderlayGuids.has(guid) || VisibilityConfig.shouldBeVisible(guid, config);
  },
);

export const getActiveSnappingGuideLines = createSelector(
  getAllSnappingUnderlaysDictionary,
  getActiveUnderlayVisibility,
  (persisted, isVisible): LineSegment2D[] =>
    Object.values(persisted)
      .filter((underlay) => isVisible(underlay.guid))
      .flatMap((underlay) =>
        computeLinesForUnderlay(underlay.extractedPaths, underlay.scale, underlay.translate, underlay.rotation),
      ),
);
